import React, { useCallback, useEffect, useRef, useState } from 'react';
import { theme } from '../styles/theme';

const STEP_HOLD_MS = 2500;
const FINAL_HOLD_MS = 3000;
const MOVE_MS = 650;
const RESET_MS = 300;
const STEP_INTERVAL_MS = STEP_HOLD_MS + MOVE_MS;
const FADE_START_MS = 3 * STEP_INTERVAL_MS + FINAL_HOLD_MS;
const CYCLE_MS = FADE_START_MS + RESET_MS;
const COMPACT_QUERY = '(max-width: 799px)';

const steps = [
  {
    title: 'Search your team',
    titleLines: ['Search your', 'team'],
    description: 'It’s already in the app, with the match details you need.',
  },
  {
    title: 'Upload your photos once',
    titleLines: ['Upload your', 'photos once'],
    description: 'Use the photos you already have, plus a photo of your jersey. We give them a studio finish for your designs.',
  },
  {
    title: 'Choose your design',
    titleLines: ['Choose your', 'design'],
    description: 'Pick the matchday look you love. We use it as inspiration to create your own design.',
  },
  {
    title: 'Your design. Their reactions.',
    titleLines: ['Your design.', 'Their reactions.'],
    description: '4KMATCH brings everything together automatically. Save your design, share it and see how people react.',
  },
];

interface SwipeGesture {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  lastTime: number;
  velocity: number;
  baseIndex: number;
  baseOffset: number;
  offset: number;
  stepWidth: number;
  axis: 'horizontal' | 'vertical' | null;
}

const clampStep = (index: number) => Math.max(0, Math.min(steps.length - 1, index));

interface ReactionWave {
  id: number;
  lastIndex: number;
  trails: React.CSSProperties[];
}

/** Sample once per activation; evenly spaced starting lanes keep the whole background involved. */
const createReactionWave = (id: number): ReactionWave => {
  let lastIndex = 0;
  let latestEnd = 0;
  const trails = Array.from({ length: 12 }, (_, index) => {
    const startX = 2 + index * 8 + Math.random() * 4;
    let direction = Math.random() < 0.5 ? -1 : 1;
    const distance = 16 + Math.random() * 16;
    if (startX + direction * distance < 2 || startX + direction * distance > 94) direction *= -1;
    const startY = 16 + Math.random() * 64;
    const endY = Math.max(8, Math.min(84, startY + (Math.random() - 0.65) * 32));
    const delay = Math.random() * 450;
    const duration = FINAL_HOLD_MS - delay - Math.random() * 200;
    if (delay + duration > latestEnd) {
      latestEnd = delay + duration;
      lastIndex = index;
    }
    return {
      '--hiw-reaction-from-x': startX + '%',
      '--hiw-reaction-to-x': startX + direction * distance + '%',
      '--hiw-reaction-from-y': startY + '%',
      '--hiw-reaction-to-y': endY + '%',
      '--hiw-reaction-from-turn': (Math.random() - 0.5) * 36 + 'deg',
      '--hiw-reaction-to-turn': (Math.random() - 0.5) * 56 + 'deg',
      '--hiw-reaction-scale': 0.8 + Math.random() * 0.35,
      '--hiw-reaction-opacity': 0.35 + Math.random() * 0.2,
      animationDelay: delay + 'ms',
      animationDuration: duration + 'ms',
      color: index % 2 ? '#438dff' : '#ff334b',
    } as React.CSSProperties;
  });
  return { id, lastIndex, trails };
};

/** One local timeline drives progress, reaction triggers and the mobile carousel together. */
export const HowItWorks: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const elapsed = useRef(0);
  const activeIndex = useRef(0);
  const gesture = useRef<SwipeGesture | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const reactionId = useRef(0);
  const [reactionWaves, setReactionWaves] = useState<ReactionWave[]>([]);
  const [inView, setInView] = useState(false);
  const [pageHidden, setPageHidden] = useState(() => document.hidden);
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [compact, setCompact] = useState(() => window.matchMedia(COMPACT_QUERY).matches);
  const [paused, setPaused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const playing = inView && !pageHidden && !reducedMotion && !paused && !dragging;

  const activateStep = useCallback((index: number, restart = false) => {
    if (activeIndex.current === index && !restart) return;
    activeIndex.current = index;
    setActiveStep(index);
    if (!reducedMotion && index === 3) {
      const wave = createReactionWave(++reactionId.current);
      // Keep the outgoing wave long enough to fade on manual re-selection.
      setReactionWaves((previous) => [...previous.slice(-1), wave]);
    }
  }, [reducedMotion]);

  const paintTimeline = useCallback((time: number) => {
    const selected = Math.min(3, Math.floor(time / STEP_INTERVAL_MS));
    const timeInStep = time - selected * STEP_INTERVAL_MS;
    const travel = selected < 3 ? Math.max(0, Math.min(1, (timeInStep - STEP_HOLD_MS) / MOVE_MS)) : 0;
    // Fast departure and a gentle arrival; selection changes only on arrival.
    const easedTravel = 1 - Math.pow(1 - travel, 3);
    const progress = (selected + easedTravel) / 3;
    const opacity = time > FADE_START_MS ? (CYCLE_MS - time) / (CYCLE_MS - FADE_START_MS) : 1;
    lineRef.current?.style.setProperty('--hiw-progress', String(progress));
    lineRef.current?.style.setProperty('--hiw-line-opacity', String(opacity));
    activateStep(selected);
  }, [activateStep]);

  const selectStep = useCallback((index: number) => {
    const selected = clampStep(index);
    const restarting = activeIndex.current === selected;
    // A swipe or click begins the selected step's complete hold, not its exit.
    elapsed.current = selected * STEP_INTERVAL_MS;
    paintTimeline(elapsed.current);
    if (restarting) activateStep(selected, true);
  }, [activateStep, paintTimeline]);

  useEffect(() => {
    const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobile = window.matchMedia(COMPACT_QUERY);
    const updateMotion = () => setReducedMotion(motion.matches);
    const updateLayout = () => setCompact(mobile.matches);
    const updateVisibility = () => setPageHidden(document.hidden);
    motion.addEventListener('change', updateMotion);
    mobile.addEventListener('change', updateLayout);
    document.addEventListener('visibilitychange', updateVisibility);
    const observer = typeof IntersectionObserver === 'undefined' ? null : new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting), { threshold: 0.15 }
    );
    if (observer && sectionRef.current) observer.observe(sectionRef.current);
    else setInView(true);
    return () => {
      observer?.disconnect();
      motion.removeEventListener('change', updateMotion);
      mobile.removeEventListener('change', updateLayout);
      document.removeEventListener('visibilitychange', updateVisibility);
    };
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const measure = () => trackRef.current?.style.setProperty('--hiw-viewport', viewport.getBoundingClientRect().width + 'px');
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(viewport);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  useEffect(() => {
    if (!playing) return;
    let previousTime = performance.now();
    let frame: number;
    const animate = (now: number) => {
      elapsed.current = (elapsed.current + Math.max(0, now - previousTime)) % CYCLE_MS;
      previousTime = now;
      paintTimeline(elapsed.current);
      frame = window.requestAnimationFrame(animate);
    };
    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, [playing, paintTimeline]);

  useEffect(() => {
    if (compact || !gesture.current) return;
    const pointerId = gesture.current.pointerId;
    gesture.current = null;
    setDragging(false);
    trackRef.current?.style.setProperty('--hiw-drag', '0px');
    if (viewportRef.current?.hasPointerCapture(pointerId)) viewportRef.current.releasePointerCapture(pointerId);
  }, [compact]);

  const startSwipe = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!compact || gesture.current || !event.isPrimary || event.button !== 0 || (event.target as HTMLElement).closest('button')) return;
    const viewport = event.currentTarget.getBoundingClientRect();
    const track = trackRef.current?.getBoundingClientRect();
    if (!track) return;
    const stepWidth = track.width / steps.length;
    if (!stepWidth) return;
    const centeredLeft = (viewport.width - stepWidth) / 2 - activeIndex.current * stepWidth;
    const baseOffset = track.left - viewport.left - centeredLeft;
    gesture.current = {
      pointerId: event.pointerId,
      startX: event.clientX, startY: event.clientY,
      lastX: event.clientX, lastTime: event.timeStamp, velocity: 0,
      baseIndex: activeIndex.current, baseOffset, offset: baseOffset, stepWidth, axis: null,
    };
    trackRef.current?.style.setProperty('--hiw-drag', baseOffset + 'px');
    setDragging(true);
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveSwipe = (event: React.PointerEvent<HTMLDivElement>) => {
    const swipe = gesture.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    const dx = event.clientX - swipe.startX;
    const dy = event.clientY - swipe.startY;
    if (!swipe.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 6) swipe.axis = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    if (swipe.axis !== 'horizontal') return;
    if (event.cancelable) event.preventDefault();
    const duration = event.timeStamp - swipe.lastTime;
    if (duration > 0) swipe.velocity = (event.clientX - swipe.lastX) / duration;
    swipe.lastX = event.clientX;
    swipe.lastTime = event.timeStamp;
    swipe.offset = Math.max(-(3 - swipe.baseIndex) * swipe.stepWidth, Math.min(swipe.baseIndex * swipe.stepWidth, swipe.baseOffset + dx));
    trackRef.current?.style.setProperty('--hiw-drag', swipe.offset + 'px');
  };

  const finishSwipe = (event: React.PointerEvent<HTMLDivElement>, cancelled = false) => {
    const swipe = gesture.current;
    if (!swipe || swipe.pointerId !== event.pointerId) return;
    gesture.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (!cancelled && swipe.axis === 'horizontal') {
      const velocity = event.timeStamp - swipe.lastTime < 100 ? swipe.velocity : 0;
      let target = Math.round(swipe.baseIndex - swipe.offset / swipe.stepWidth);
      const distance = event.clientX - swipe.startX;
      if (target === swipe.baseIndex && Math.abs(distance) > 12 && (Math.abs(distance) > swipe.stepWidth * 0.2 || Math.abs(velocity) > 0.45)) {
        target += distance < 0 ? 1 : -1;
      }
      selectStep(target);
    }
    trackRef.current?.style.setProperty('--hiw-drag', '0px');
    setDragging(false);
  };

  return (
    <section id="how-it-works" ref={sectionRef} className="hiw-section" aria-labelledby="hiw-heading" data-playing={playing}>
      <div className="hiw-inner">
        <div className="hiw-header">
          <h2 id="hiw-heading">How it works</h2>
          <p>Your matchday, made simple.</p>
      <div className="hiw-reaction-field" aria-hidden="true">
        {reactionWaves.map((wave) => (
          <div key={wave.id} className="hiw-reaction-wave" data-active={activeStep === 3 && wave.id === reactionId.current}>
            {wave.trails.map((style, index) => (
              <span
                key={index}
                className="hiw-reaction-trail"
                style={style}
                onAnimationEnd={index === wave.lastIndex ? (event) => {
                  if (event.target === event.currentTarget && event.animationName === 'hiwReactionDrift') {
                    setReactionWaves((previous) => previous.filter((item) => item.id !== wave.id));
                  }
                } : undefined}
              >
                <svg className="hiw-reaction-icon" viewBox="0 0 24 24" fill="currentColor">
                  {index % 2 ? (
                    <path d="M14 9V4a3 3 0 0 0-3-3L7 10v12h11.3a2 2 0 0 0 2-1.7l1.4-9A2 2 0 0 0 19.7 9H14ZM2 10h3v12H2z" />
                  ) : (
                    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
                  )}
                </svg>
              </span>
            ))}
          </div>
        ))}
      </div>
        </div>
        <div
          ref={viewportRef}
          className={'hiw-viewport' + (dragging ? ' hiw-dragging' : '')}
          role="group"
          aria-label={`How it works steps. Arrow keys select steps; Space pauses or resumes automatic playback. Automatic playback ${paused ? 'paused' : 'enabled'}.`}
          aria-roledescription={compact ? 'carousel' : undefined}
          tabIndex={0}
          onPointerDown={startSwipe}
          onPointerMove={moveSwipe}
          onPointerUp={(event) => finishSwipe(event)}
          onPointerCancel={(event) => finishSwipe(event, true)}
          onLostPointerCapture={(event) => finishSwipe(event, true)}
          onDragStart={(event) => event.preventDefault()}
          onKeyDown={(event) => {
            if (event.target === event.currentTarget && event.key === ' ') {
              event.preventDefault();
              if (!event.repeat) setPaused(value => !value);
              return;
            }
            if (gesture.current) return;
            if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
              event.preventDefault();
              selectStep(event.key === 'Home' ? 0 : event.key === 'End' ? 3 : activeIndex.current + (event.key === 'ArrowRight' ? 1 : -1));
            }
          }}
        >
          <div ref={trackRef} className="hiw-track" style={{ '--hiw-active': activeStep } as React.CSSProperties}>
            <div ref={lineRef} className="hiw-progress-line" aria-hidden="true">
              <div className="hiw-progress-fill" />
              <div className="hiw-progress-tip" />
            </div>
            <ol className="hiw-steps">
              {steps.map((step, index) => (
                <li key={step.title} className={'hiw-step' + (index === activeStep ? ' hiw-current' : '') + (index < activeStep || index === 0 ? ' hiw-completed' : '')} aria-current={index === activeStep ? 'step' : undefined}>
                  <button type="button" className="hiw-badge" aria-label={'Step ' + (index + 1) + ': ' + step.title} onClick={() => selectStep(index)}>{index + 1}</button>
                  <h3>{step.titleLines[0]}{' '}<br />{step.titleLines[1]}</h3>
                  <p>{step.description}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>

      </div>
      <style>{`
        .hiw-section {
          --hiw-reaction-reach: 100px;
          --hiw-section-top: 32px;
          --hiw-heading-size: 36px;
          --hiw-subtitle-size: 16px;
          --hiw-reaction-size: clamp(22px, 2.3vw, 35px);
          --hiw-badge-size: clamp(36px, 2.3vw, 50px);
          --hiw-column-gap: clamp(28px, 4.8vw, 104px);
          position: relative;
          isolation: isolate;
          /* Bounded decorative overlap; horizontal carousel clipping remains local. */
          overflow: visible;
          clip-path: inset(calc(-1 * var(--hiw-reaction-reach)) 0 0);
          margin-top: 0;
          padding: 32px clamp(24px, 4vw, 88px) 24px;
          background: #050505;
          color: #fff;
          font-family: ${theme.fonts.primary};
        }
        .hiw-section .hiw-inner { position: relative; z-index: 1; max-width: 2016px; margin: 0 auto; }
        .hiw-section .hiw-header { position: relative; z-index: 1; text-align: center; padding: 0; margin-bottom: 32px; }
        .hiw-section .hiw-header > h2, .hiw-section .hiw-header > p { position: relative; z-index: 1; }
        .hiw-section .hiw-header h2 { margin: 0 0 8px; font-family: ${theme.fonts.heading}; font-size: var(--hiw-heading-size); font-weight: 650; line-height: 1.15; letter-spacing: -.025em; }
        .hiw-section .hiw-header p { margin: 0; color: #b9b7bc; font-size: var(--hiw-subtitle-size); font-weight: 400; line-height: 1.45; letter-spacing: normal; }
        .hiw-section .hiw-viewport { position: relative; z-index: 1; padding: 0; }
        .hiw-section .hiw-track { --hiw-active: 0; --hiw-drag: 0px; position: relative; width: 100%; }
        .hiw-section .hiw-progress-line { --hiw-progress: 0; --hiw-line-opacity: 1; position: absolute; top: calc((var(--hiw-badge-size) - 6px) / 2); left: calc(var(--hiw-badge-size) / 2); right: calc((100% - 3 * var(--hiw-column-gap)) / 4 - var(--hiw-badge-size) / 2); height: 6px; border-radius: 10px; background: #242225; }
        .hiw-section .hiw-progress-fill { position: absolute; inset: 0; border-radius: inherit; transform: scaleX(var(--hiw-progress)); transform-origin: left; opacity: var(--hiw-line-opacity); background: linear-gradient(90deg, #ff332c, #ff2475 58%, #ff51b0); box-shadow: 0 0 15px rgba(255, 39, 100, .25); will-change: transform; }
        .hiw-section .hiw-progress-tip { position: absolute; top: 50%; left: calc(var(--hiw-progress) * 100%); width: 18px; height: 10px; border-radius: 50%; transform: translate(-75%, -50%); opacity: var(--hiw-line-opacity); background: #ff61b8; box-shadow: 0 0 18px rgba(255, 39, 143, .6); }
        .hiw-section .hiw-steps { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: var(--hiw-column-gap); list-style: none; margin: 0; padding: 0; }
        .hiw-section .hiw-step { position: relative; display: flex; flex-direction: column; align-items: flex-start; min-width: 0; padding: 0; text-align: left; }
        .hiw-section .hiw-badge { position: relative; z-index: 2; display: grid; place-items: center; flex-shrink: 0; width: var(--hiw-badge-size); height: var(--hiw-badge-size); padding: 0; border: 1px solid rgba(255,255,255,.04); border-radius: 50%; background: linear-gradient(145deg, #303032, #222224); color: #f1f0f2; font: 650 16px/1 ${theme.fonts.primary}; cursor: pointer; transition: background 400ms cubic-bezier(.4,0,.2,1), border-color 400ms ease, color 400ms ease, box-shadow 400ms ease; }
        .hiw-section .hiw-current .hiw-badge { color: #fff; border-color: transparent; background: linear-gradient(135deg, #ff3474, #ff1262); box-shadow: 0 0 24px rgba(255, 39, 107, .14); }
        .hiw-section .hiw-step h3 { position: relative; z-index: 1; margin: 16px 0 8px; color: #f6f5f7; font-size: 20px; font-weight: 700; line-height: 1.18; letter-spacing: -.02em; }
        .hiw-section .hiw-step p { position: relative; z-index: 1; max-width: 100%; margin: 0; color: #bdbbc0; font-size: 16px; font-weight: 400; line-height: 1.38; letter-spacing: normal; }
        .hiw-section .hiw-reaction-field { position: absolute; z-index: 0; top: calc(-1 * (var(--hiw-reaction-reach) + var(--hiw-section-top))); bottom: 0; left: 0; right: 0; overflow: hidden; pointer-events: none; opacity: .65; mask-image: linear-gradient(transparent, #000 25%, #000 75%, transparent); }
        .hiw-section .hiw-reaction-wave { position: absolute; inset: 0; opacity: 1; transition: opacity 550ms cubic-bezier(.4,0,.2,1); }
        .hiw-section .hiw-reaction-wave[data-active="false"] { opacity: 0; }
        .hiw-section .hiw-reaction-trail { position: absolute; inset: 0; animation: hiwReactionDrift 3000ms cubic-bezier(.25,.3,.55,1) both; }
        .hiw-section .hiw-reaction-icon { display: block; width: var(--hiw-reaction-size); height: var(--hiw-reaction-size); margin: calc(var(--hiw-reaction-size) / -2); opacity: 0; filter: drop-shadow(0 0 3px currentColor); animation: hiwReactionAppear 3000ms cubic-bezier(.4,0,.2,1) both; animation-duration: inherit; animation-delay: inherit; }
        .hiw-section[data-playing="false"] .hiw-reaction-trail, .hiw-section[data-playing="false"] .hiw-reaction-icon { animation-play-state: paused; }
        .hiw-section .hiw-badge:focus-visible, .hiw-section .hiw-viewport:focus-visible { outline: 2px solid #ff3884; outline-offset: 5px; }
        @keyframes hiwReactionDrift {
          from { transform: translate3d(var(--hiw-reaction-from-x), var(--hiw-reaction-from-y), 0); }
          to { transform: translate3d(var(--hiw-reaction-to-x), var(--hiw-reaction-to-y), 0); }
        }
        @keyframes hiwReactionAppear {
          0% { opacity: 0; transform: rotate(var(--hiw-reaction-from-turn)) scale(calc(var(--hiw-reaction-scale) * .78)); }
          22% { opacity: var(--hiw-reaction-opacity); transform: rotate(var(--hiw-reaction-from-turn)) scale(var(--hiw-reaction-scale)); }
          70% { opacity: calc(var(--hiw-reaction-opacity) * .85); transform: rotate(var(--hiw-reaction-to-turn)) scale(var(--hiw-reaction-scale)); }
          100% { opacity: 0; transform: rotate(var(--hiw-reaction-to-turn)) scale(calc(var(--hiw-reaction-scale) * .94)); }
        }
        @media (max-width: 799px) {
          .hiw-section { --hiw-reaction-reach: 50px; --hiw-section-top: 24px; --hiw-heading-size: 24px; --hiw-subtitle-size: 16px; --hiw-reaction-size: clamp(22px, 4.1vw, 28.5px); --hiw-badge-size: 40px; --hiw-column-gap: 0px; padding: 24px 0; }
          .hiw-section .hiw-header { padding: 0 16px; margin-bottom: 32px; }
          .hiw-section .hiw-viewport { overflow: hidden; touch-action: pan-y pinch-zoom; user-select: none; -webkit-user-select: none; cursor: grab; }
          .hiw-section .hiw-track { --hiw-card-width: clamp(220px, 76vw, 300px); width: calc(4 * var(--hiw-card-width)); transform: translateX(calc((var(--hiw-viewport, 100vw) - var(--hiw-card-width)) / 2 - var(--hiw-active) * var(--hiw-card-width) + var(--hiw-drag))); transition: transform 400ms cubic-bezier(.4,0,.2,1); will-change: transform; }
          .hiw-section .hiw-dragging { cursor: grabbing; }
          .hiw-section .hiw-dragging .hiw-track { transition: none; }
          .hiw-section .hiw-step { padding: 0 20px; }
          .hiw-section .hiw-step h3 { font-size: 20px; }
          .hiw-section .hiw-step p { font-size: 16px; max-width: 244px; line-height: 1.5; }
          .hiw-section .hiw-progress-line { left: calc(20px + var(--hiw-badge-size) / 2); right: calc(var(--hiw-card-width) - 20px - var(--hiw-badge-size) / 2); }
        }
        @media (prefers-reduced-motion: reduce) {
          .hiw-section .hiw-track, .hiw-section .hiw-badge { transition: none; }
          .hiw-section .hiw-reaction-field { display: none; }
        }
      `}</style>
    </section>
  );
};

export default HowItWorks;
